import { readStreamAsText } from '../StreamReader';

export class HttpError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.statusCode = statusCode;
  }
}

export type ResponseParser<R> = (res: Response) => Promise<R>;

enum EventKeys {
  DATA = 'data',
  EVENT = 'event',
  ID = 'id'
}

const EVENT_NUMBER_OF_LINES = 4;

export type ServerSentEvent<T> = {
  data: T;
  event: string;
  id: string;
};

export const jsonResponseParser = <R>(res: Response) => {
  if (!res.ok) {
    throw new HttpError(res.statusText, res.status);
  }
  return res.json() as Promise<R>;
};

export const textStreamingResponseParser: ResponseParser<string> = async (
  res: Response
) => {
  if (!res.ok) {
    throw new HttpError(res.statusText, res.status);
  }

  if (!res.body) {
    throw new Error('Body not present');
  }

  const text = await readStreamAsText(res.body);

  return text;
};

export const failureOnStatusCodeFactory =
  (errorStatusCodes: number[]): ResponseParser<boolean> =>
  async (res: Response) => {
    if (errorStatusCodes.includes(res.status) || !res.ok) {
      throw new HttpError(res.statusText, res.status);
    }
    return true;
  };

export const emptyResponseParser: ResponseParser<void> = async (
  res: Response
) => {
  if (!res.ok) {
    throw new HttpError(res.statusText, res.status);
  }
};

export const serverSideEventsStreamingResponseParser =
  <T>(onMessage: (message: ServerSentEvent<T>) => void) =>
  async (res: Response) => {
    if (!res.ok) {
      throw new HttpError(res.statusText, res.status);
    }

    if (!res.body) {
      throw new Error('Body not present');
    }

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      buffer += value;
      const { events, remainder } = getEventsFromChunk(buffer);
      buffer = remainder;
      try {
        for (const event of events) {
          const parsedServerEvent = parseServerSideEvents<T>(event);
          onMessage(parsedServerEvent);
        }
      } catch (e) {
        console.error('Error parsing chunk:', e);
        throw e;
      }
    }
  };

const getEventsFromChunk = (
  chunk: string
): { events: string[][]; remainder: string } => {
  const eventList = chunk.split('\n\n');
  const events: string[][] = [];
  let remainder = '';

  for (const event of eventList) {
    const lines = event.split('\n');
    if (lines.length === EVENT_NUMBER_OF_LINES) {
      events.push(lines);
    } else {
      remainder += event;
    }
  }

  return { events, remainder };
};

const parseServerSideEvents = <T>(
  serverEvent: string[]
): ServerSentEvent<T> => {
  return serverEvent.filter(Boolean).reduce((acc, line) => {
    Object.values(EventKeys).forEach((eventKey) => {
      if (line.startsWith(eventKey)) {
        const eventValue = line.replace(`${eventKey}:`, '').trim();
        try {
          acc[eventKey] =
            eventKey === EventKeys.DATA ? JSON.parse(eventValue) : eventValue;
        } catch {
          if (eventKey === EventKeys.DATA) {
            acc[eventKey] = {} as T;
          } else {
            acc[eventKey] = '';
          }
        }
      }
    });

    return acc;
  }, {} as ServerSentEvent<T>);
};
